// add-unique-constraint / drop-unique-constraint edit planner (v2 Slice B3).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice B3.
//
// Both ops append a fresh ALTER TABLE statement after the source statement
// (DSE2-7: no coalescing by default; opt-in coalescing is slice H1). Source
// path may be either ALTER TABLE or CREATE TABLE — the synthesized statement
// targets the same table named by the source structure.

import { rangeFromOffsets } from '../../../sql-grammar/document/lineIndex';
import type { QualifiedName } from '../../../sql-grammar/model';
import type { DdlEdit, DdlStructure, PlannedByteEdit, UniqueConstraintDef } from '../../types';

import { pickAppendInsertion } from './appendInsertion';
import type { DdlEditCtx } from './applyDdlEdit';
import { sourceSpellingOrigin } from './columnEdit.alterTable';
import {
  synthesizeAddUniqueConstraint,
  synthesizeDropUniqueConstraint,
} from './dialect.unique';
import {
  type ColumnListOrigins,
  type DecodedColumnList,
  decodeTypedColumnList,
} from './existingNameEvidence';

type UniqueConstraintEdit = Extract<
  DdlEdit,
  { op: 'add-unique-constraint' | 'drop-unique-constraint' }
>;

export function planUniqueConstraintEdit(
  ctx: DdlEditCtx,
  edit: UniqueConstraintEdit,
): readonly PlannedByteEdit[] {
  const table = pickTable(ctx.structure);
  if (!table) {
    throw new Error(
      `applyDdlEdit: cannot apply ${edit.op} edit to ${ctx.structure.kind} statement`,
    );
  }
  const synth = edit.op === 'add-unique-constraint'
    ? synthesizeAdd(ctx, table, edit.unique)
    : synthesizeDropUniqueConstraint(ctx.engine, table, edit.constraintName, edit.cascade);

  const { offset: insertAt, sep, trailSep } = pickAppendInsertion(
    ctx.source,
    ctx.statementInfo.range.endOffset,
  );
  return [{
    range: rangeFromOffsets(ctx.lineIdx, insertAt, insertAt),
    replacement: sep + synth + trailSep,
    rationale: `${edit.op}:alter-table`,
  }];
}

function pickTable(s: DdlStructure): QualifiedName | undefined {
  if (s.kind === 'alter-table') return s.table;
  if (s.kind === 'create-table') return s.table;
  return undefined;
}

/** Decode the caller's column lists, then hand the synthesizer names plus the
 *  origin each one answers with — the same two-step `primaryKeyEdit.ts` runs,
 *  because a UNIQUE constraint names existing columns under the identical
 *  contract. Both halves happen HERE, at the edit entry point, because they
 *  are one decision: the delimiters a user typed are simultaneously the thing
 *  to strip from the name and the reason the stripped bytes are the catalog's.
 *
 *  The constraint is emitted against `pickTable(ctx.structure)` — the SOURCE
 *  statement's own table — so that statement's clauses (or a CREATE TABLE's
 *  column list) answer directly for every column it lists.
 *
 *  KEY and INCLUDE answer SEPARATELY, and each answers BY POSITION — the same
 *  split `primaryKeyEdit.ts` makes, for the same two reasons: a resolver
 *  shared between the lists gives the KEY element the INCLUDE element's typed
 *  delimiters and the constraint lands on another column — silently, since
 *  PostgreSQL accepts an INCLUDE that overlaps its own key — and within one
 *  list only the position tells `Id` and `"Id"` apart. */
function synthesizeAdd(
  ctx: DdlEditCtx,
  table: QualifiedName,
  uq: UniqueConstraintDef,
): string {
  const keys = decodeTypedColumnList(ctx.engine, uq.columns);
  const include = uq.includeColumns !== undefined
    ? decodeTypedColumnList(ctx.engine, uq.includeColumns)
    : undefined;
  const decoded: UniqueConstraintDef = {
    ...uq,
    columns: keys.columns,
    ...(include !== undefined ? { includeColumns: include.columns } : {}),
  };
  return synthesizeAddUniqueConstraint(ctx.engine, table, decoded, {
    quoteName: table.quoted,
    columnNameOrigin: originsOf(ctx, keys),
    ...(include !== undefined ? { includeColumnNameOrigin: originsOf(ctx, include) } : {}),
  });
}

/** One list's per-position answer: the element's own typed delimiters first,
 *  then the source statement's evidence about the name it decoded to. */
function originsOf(ctx: DdlEditCtx, list: DecodedColumnList): ColumnListOrigins {
  return (index) => list.typedOriginAt(index)
    ?? sourceSpellingOrigin(ctx.structure, list.columns[index]);
}
